import { Injectable, inject, signal, computed, Signal, WritableSignal } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventType, EventMessage, AccountInfo } from '@azure/msal-browser';
import { tap } from 'rxjs';
import { TokenClaims } from 'src/app/shared/interfaces/token-claims.interface';
import { UserRole } from 'src/app/shared/interfaces/user-role.enum';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private msalService = inject(MsalService);
    private msalBroadcastService = inject(MsalBroadcastService);

    private _isAuthenticated: WritableSignal<boolean> = signal<boolean>(false);
    private _isAuthLoading: WritableSignal<boolean> = signal<boolean>(true);
    public readonly isAdmin: Signal<boolean> = computed(
        () => this.userIdTokenClaims()?.roles?.includes(UserRole.ADMIN) || false
    );

    private _userAccount: WritableSignal<AccountInfo | null> = signal<AccountInfo | null>(null);

    public isAuthenticated: Signal<boolean> = computed(() => this._isAuthenticated());
    public isAuthLoading: Signal<boolean> = computed(() => this._isAuthLoading());
    public idToken: Signal<string | null> = computed(() => this._userAccount()?.idToken || null);
    public userIdTokenClaims: Signal<TokenClaims | null> = computed(
        () => this._userAccount()?.idTokenClaims as TokenClaims
    );
    public userName: Signal<string | null> = computed(
        () => `${this.userIdTokenClaims()?.preferred_family_name}, ${this.userIdTokenClaims()?.preferred_given_name}`
    );
    public orgCode: Signal<string | null> = computed(() => this.userIdTokenClaims()?.org_code || null);

    constructor() {
        this.initializeAuth();
    }

    public logout(): void {
        this.msalService.logoutRedirect();
    }

    private initializeAuth(): void {
        this.subscribeToLogin();
        this.subscribeToLogout();
    }

    private subscribeToLogin(): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                tap((status: InteractionStatus) => {
                    this._isAuthLoading.set(status !== InteractionStatus.None);

                    if (status === InteractionStatus.None) {
                        this.processLoginDoneAndUpdateUserData();
                    }
                })
            )
            .subscribe();
    }

    private subscribeToLogout(): void {
        this.msalBroadcastService.msalSubject$
            .pipe(
                tap((msg: EventMessage) => {
                    if (msg.eventType === EventType.LOGOUT_SUCCESS) {
                        this._isAuthenticated.set(false);
                        this._userAccount.set(null);
                    }
                })
            )
            .subscribe();
    }

    private processLoginDoneAndUpdateUserData(): void {
        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length > 0) {
            const activeAccount = this.msalService.instance.getActiveAccount() || accounts[0];

            if (activeAccount) {
                this.msalService.instance.setActiveAccount(activeAccount);
                this._userAccount.set(activeAccount);
                this._isAuthenticated.set(true);
            } else {
                this.setUnauthorizedUserSignals();
            }
        } else {
            this.setUnauthorizedUserSignals();
        }

        this._isAuthLoading.set(false);
    }

    private setUnauthorizedUserSignals() {
        this._isAuthenticated.set(false);
        this._userAccount.set(null);
    }
}
